<template>
  <div class="cbg-auth__field-wrapper">
    <div :class="['cbg-auth__field', additionalClasses]">
      <input class="cbg-auth__field-input"
             ref="inputField"
             :id="inputName"
             :name="inputName"
             placeholder=" "
             autocomplete="off"
             :type="inputType"
             :value="modelValue || ''"
             @focus="handleFocus"
             @focusout="handleBlur"
             @input="validateInput"
      />
      <label class="cbg-auth__field-label" :for="inputName" @click="handleFocus">{{inputName}} *</label>
      <div class="cbg-auth__field-icons">
        <button v-if="modelValue" class="cbg-auth__field-button" type="button" @click.prevent="toggleVisible">
          <Svg class-name="cbg-auth__field-button__ico" :icon-id="buttonIconId"/>
        </button>
        <Svg v-if="isSuccessful" class-name="cbg-auth__field-icons__success" icon-id="#success"/>
      </div>
    </div>
    <div v-if="isNew && isFocused" class="cbg-auth__field-validation">
      <p class="cbg-auth__field-validation__title">Create a password that:</p>
      <ul class="cbg-auth__field-validation__list">
        <li class="cbg-auth__field-validation__item" :class="{ 'cbg-auth__field-validation__item_valid': rule.isValid}" v-for="(rule, index) in rules" :key="index">{{rule.message}}</li>
      </ul>
    </div>
    <p v-if="isError || isWrongRequest || isSameRequest" class="cbg-auth__error">
      <Svg class-name="cbg-auth__error-ico" icon-id="#warning"/>
      <span v-if="isError || isWrongRequest" class="cbg-auth__error-text">Invalid password</span>
      <span v-else class="cbg-auth__error-text">New password must be different from the old one</span>
    </p>
  </div>
</template>

<script>
import Svg from "@/components/global/Svg.vue";
import {ContextUpdateSymbol, ContextStateSymbol} from "@/components/global/Context.vue";
export default {
  name: 'PasswordInput',
  components: {
    Svg
  },
  props: {
    className: String,
    inputName: String,
    modelValue: String,
    emailString: String,
    dataType: {
      type: String,
      default: 'text',
    },
    confirmPassword: String,
    isNew: {
      type: Boolean,
      default: false,
    },
    isWrong: {
      type: Boolean,
      default: false,
    },
    isSame: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isWrongRequest: this.isWrong,
      isSameRequest: this.isSame,
      isVisible: false,
      isFocused: false,
      isBlocked: this.modelValue,
      isValid: true,
      rules: [],
    };
  },
  inject: {
    context: { from: ContextStateSymbol, default: null },
    updateContext: { from: ContextUpdateSymbol, default: null },
  },
  computed: {
    inputType() {
      return this.isVisible ? 'text' : 'password';
    },
    buttonIconId() {
      return `#${this.isVisible ? 'open-eye' : 'close-eye'}`;
    },
    additionalClasses() {
      return [
        this.className,
        {
          'cbg-auth__field_focused': this.isFocused,
          'cbg-auth__field_locked': this.isBlocked,
          'cbg-auth__field_invalid': !this.isValid || this.isWrongRequest || this.isSameRequest,
        }
      ];
    },
    isSuccessful() {
      return this.isValid && this.modelValue && !this.isNew && !this.isWrongRequest && !this.isSameRequest || this.isValid && this.modelValue && !this.isFocused && !this.isWrongRequest && !this.isSameRequest;
    },
    isError() {
      return !this.isValid && !this.isNew || !this.isValid && !this.isFocused && this.isWrongRequest && !this.isSameRequest;
    }
  },
  watch: {
    confirmPassword: function () {
      this.modelValue && this.validateInput({
        target: {
          value: this.modelValue,
        }
      });
    },
    emailString: function() {
      this.updateRules();

      if(this.isNew && this.emailString && this.modelValue) {
        this.validateInput();
      }
    },
    isWrong: function(newValue) {
      this.isWrongRequest = newValue;
    },
    isSame: function(newValue) {
      this.isSameRequest = newValue;
    },
  },
  methods: {
    toggleVisible() {
      this.isVisible = !this.isVisible;
    },

    handleFocus() {
      this.isFocused = true;
      this.isBlocked = false;
      this.isWrongRequest = false;
      this.isSameRequest = false;
    },

    handleBlur() {
      this.isFocused = false;
      this.isBlocked = !!this.modelValue;
    },

    validateInput(event) {
      const userPassword = event ? event.target.value : this.$refs.inputField.value;

      if (this.confirmPassword) {
        this.isValid = userPassword === this.confirmPassword;
      } else {
        this.rules.forEach(rule => (rule.isValid = rule.regex.test(userPassword)));
        this.isValid = this.rules.every(rule => rule.isValid);
      }

      this.$emit('update:modelValue', userPassword);
    },

    updateRules() {
      this.rules = [
        { message: 'contains 8-20 characters', regex: /^.{8,20}$/, isValid: false },
        { message: 'contains both lower (a-z) and upper case letters (A-Z)', regex: /^(?=.*[a-z])(?=.*[A-Z]).+$/, isValid: false },
        { message: 'contains at least one uppercase (A-Z) letter', regex: /.*[A-Z].*/, isValid: false },
        { message: 'contains at least one number (0-9)', regex: /.*\d.*/, isValid: false },
        { message: 'does not contain your email address', regex: this.emailString ? new RegExp(`^(?!.*${this.emailString}).*`) : new RegExp('.?'), isValid: true },
      ];
    },
  },
  created() {
    this.updateRules();
  },
}
</script>